import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MessageSquare, RefreshCcw, Plus, Send, User, Clock, Hash } from "lucide-react";
import { useAuth } from '../services/AuthContext.js'; // Import your AuthContext
import "./forum.css";
import { useNavigate } from "react-router-dom";
import { getAnyUserData } from "../services/user_info.js";

// Set the axios base URL
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;
const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

function Forum() {
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTopicTitle, setSelectedTopicTitle] = useState("");

  return (
    <div className="forum-page">
      <div className="forum-container">
        <div className="forum-header">
          <h1 className="forum-title">
            <MessageSquare className="forum-title-icon" size={40} />
            Fórum de Discussão
          </h1>
          <p className="forum-subtitle">Conecte, discuta e compartilhe com a nossa comunidade</p>
        </div>
        
        <div className="forum-layout">
          <div className="forum-topics-section">
            <ForumTopics
              onSelectTopic={(id, title) => {
                setSelectedTopicId(id);
                setSelectedTopicTitle(title);
              }}
              selectedTopicId={selectedTopicId}
            />
          </div>
          <div className="forum-messages-section">
            <TopicMessages topicId={selectedTopicId} topicTitle={selectedTopicTitle} />
          </div>
        </div>
      </div>
    </div>
  );
}

const ProfileImage = ({ src, alt }) => {
  const default_profile_image_url = "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";
  const [imgSrc, setImgSrc] = useState(src || default_profile_image_url);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className="message-avatar"
      onError={() => setImgSrc(default_profile_image_url)}
    />
  );
};

function ForumTopics() {
  const {user, getIdToken} = useAuth();

  const [usernames, setUsernames] = useState({});
  const [topics, setTopics] = useState([]);
  const [title, setTitle] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const navigate = useNavigate();

  const getUserName = async (user_id) => {
    const token = await getIdToken();
    const data = await getAnyUserData(token, user_id);
    console.log("Name:", data.full_name);
    return data.full_name || "Usuário";
  }

  useEffect(() => {
    const uniqueUserIds = [...new Set(topics.map(topic => topic.CreatedBy))];

    uniqueUserIds.forEach(async (userId) => {
      if (!usernames[userId]) {
        try {
          const name = await getUserName(userId);
          setUsernames(prev => ({ ...prev, [userId]: name }));
        } catch (e) {
          console.error(`Erro ao buscar nome de ${userId}:`, e);
          setUsernames(prev => ({ ...prev, [userId]: 'Usuário' }));
        }
      }
    });
  }, [topics]);

  useEffect(() => {
    fetchTopics();
    setCreatedBy(user.cognitoUser.username);
    getUserName(user.cognitoUser.username);
    if (user.cognitoUser.username === "c14bb510-e0b1-7037-0380-c38405c3b7eb") {
      setIsAdmin(true);
    }
  }, [user]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/forum/get-forum-topics`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
      });
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      console.error("Error fetching topics:", err);
      setError("Não foi possível carregar os tópicos");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSubmit = async () => {
    if (!title.trim() || !createdBy.trim()) return;
    
    try {
      setError(null);
      const token = await getIdToken();
      const payload = {
        title: title, 
        created_by: createdBy 
      };
      const responsePost = await fetch(`${baseAPI_URL}/forum/create-forum-topic`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      setTitle("");
      setCreatedBy("");
      setShowForm(false);
      fetchTopics(); // Refresh the topics list
    } catch (err) {
      console.error("Error creating topic:", err);
      setError("Não foi possível criar o tópico");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // use 24-hour format
    });
  };

  return (
    <div className="topics-container">
      <div className="topics-header">
        <div className="topics-header-content">
          <h2 className="topics-title">
            <Hash size={20} />
            Tópicos
          </h2>
          { isAdmin && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="topics-new-btn"
            >
              <Plus size={16} />
              Novo Tópico
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showForm && (
        <div className="topics-form">
          <div className="topics-form-content">
            <div className="form-group">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Qual o assunto do tópico?"
                className="form-input"
              />
            </div>
            <div className="form-actions">
              <button
                onClick={handleTopicSubmit}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Criar Tópico
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="topics-list">
        {loading ? (
          <div className="loading-message">Carregando tópicos...</div>
        ) : topics.length === 0 ? (
          <div className="empty-message">Nenhum tópico até agora. Crie o primeiro!</div>
        ) : (
          topics.map((topic) => (
            <div
              key={topic.TopicId}
              onClick={() => {navigate(`/topic/${topic.TopicId}`);}}
              className={`topic-item`}
            >
              <div className="topic-item-header">
                <h3 className="topic-title">{topic.Title}</h3>
                <span className="topic-message-count">{topic.MessageCount || 0}</span>
              </div>
              <div className="topic-meta">
                <div className="topic-author">
                  <User size={14} />
                  <span>{usernames[topic.CreatedBy] || '...'}</span>
                </div>
                <div className="topic-date">
                  <Clock size={14} />
                  <span>{formatDate(topic.CreatedAt)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function TopicMessages({ topicId }) {
  const { user, getIdToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [topicTitle, setTopicTitle] = useState("Tópico sem título");

  const [usernames, setUsernames] = useState({});

  const navigate = useNavigate();

  const scrollContainerRef = useRef(null);
  
    useEffect(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        container.scrollTop = container.scrollHeight;
      }
    }, [messages]);

  useEffect(() => {
    const uniqueUserIds = [...new Set(messages.map(msg => msg.UserId))];

    uniqueUserIds.forEach(async (userId) => {
      if (!usernames[userId]) {
        try {
          const name = await getUserName(userId);
          setUsernames(prev => ({ ...prev, [userId]: name }));
        } catch (e) {
          console.error(`Erro ao buscar nome de ${userId}:`, e);
          setUsernames(prev => ({ ...prev, [userId]: 'Usuário' }));
        }
      }
    });
  }, [messages]);

  useEffect(() => {
    if (topicId) {
      fetchMessages();
    } else {
      setMessages([]);
    }
  }, [topicId]);

  const getUserName = async (user_id) => {
    const token = await getIdToken();
    const data = await getAnyUserData(token, user_id);
    return data.full_name || "Usuário";
  }

  useEffect(() => {
    // Set userId from authentication context
    setUserId(user.cognitoUser.username);
    //setUserId(user?.attributes?.name || user?.getUsername?.() || ""); //this is for name
  }, [user]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/forum/get-forum-messages/${topicId}`, {
        headers: {
            Authorization: `Bearer ${token}`
          }
      });
      const data = await response.json();
      setMessages(data);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Não foi possível carregar as mensagens");
    } finally {
      setLoading(false);
    }
  };

  const getTopicTitle = async (topicId) => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/forum/get-forum-topic/${topicId}`, {
        headers: {
            Authorization: `Bearer ${token}`
          }
      });
      const data = await response.json();
      return data.Title || "Tópico sem título";
    } catch (err) {
      console.error("Error fetching topic title:", err);
      return "Tópico sem título";
    }
  };
  useEffect(() => {
    const fetchTopicTitle = async () => {
      if (topicId) {
        const title = await getTopicTitle(topicId);
        setTopicTitle(title);
      } else {
        setTopicTitle("Tópico sem título");
      }
    };
    fetchTopicTitle();
  }, [topicId]);

  const handleMessageSubmit = async () => {
    if (!content.trim() || !userId) return;
    
    try {
      setError(null);
      const token = await getIdToken();
      const payload = {
        topic_id: topicId,
        user_id: userId,
        content: content,
      };
      const responsePost = await fetch(`${baseAPI_URL}/forum/add-forum-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      setContent("");
      fetchMessages(); // Refresh the messages list
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Não foi possível enviar a mensagem");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // use 24-hour format
    });
  };

  if (!topicId) {
    return (
      <div className="messages-container">
        <div className="messages-empty">
          <MessageSquare size={64} className="messages-empty-icon" />
          <h3 className="messages-empty-title">Selecione um tópico</h3>
          <p className="messages-empty-text">Selecione um tópico para entrar na discussão.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="messages-header">
        <h2 className="messages-title">
          <MessageSquare size={20} />
          {topicTitle} - 
          <a href="/forum">Ver todos os tópicos</a>
        </h2>
      </div>

      <div className="messages-content" ref={scrollContainerRef}>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="loading-message">Carregando mensagens...</div>
        ) : messages.length === 0 ? (
          <div className="messages-empty">
            <MessageSquare size={48} className="messages-empty-icon" />
            <p className="messages-empty-text">Sem mensagens ainda. Seja o primeiro a comentar!</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg) => (
              <div key={msg.MessageId} className="message-item">
                <div className="message-header">

                  <div className="message-author">
                    <ProfileImage
                      src={msg.profile_photo}
                      alt={usernames[msg.UserId]}
                    />
                    <a href={`/user/${msg.slug}`} target="_blank">
                      <span className="comment-author">{msg.full_name}</span>
                    </a>
                  </div>
                  <div className="message-date">
                    <Clock size={12} />
                    <span>{formatDate(msg.CreatedAt)}</span>
                  </div>
                </div>
                <p className="message-content">{msg.Content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="messages-form">
        <div className="messages-form-content">
          <div className="message-input-wrapper">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Digite a sua mensagem..."
              className="message-input"
              rows="2"
            />
          </div>
          <div className="buttons-wrapper">
              <button
                onClick={fetchMessages}
                className="message-send-btn"
              >
                <RefreshCcw size={20} />
              </button>
              <button
                onClick={handleMessageSubmit}
                className="message-send-btn"
              >
                <Send size={20} />
              </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Forum, ForumTopics, TopicMessages };